// =====================================================
// Authentication Context
// Digital Map - Victim Services Mapping Application
// =====================================================

import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, ReactNode } from 'react';
import { authAPI, RegisterData } from '../lib/authApi';
import { setAuthFlag, clearAuthFlag, isAuthFlagSet } from '../lib/authFlag';
import { AuthError, AuthState, MfaLoginResponse, User, ValidationError, AUTH_ERROR_CODES } from '../types/auth';
import { ErrorHandler } from '../utils/errorHandler';

// =====================================================
// TYPES
// =====================================================

// Re-export types from auth types file
export type { User, AuthState, AuthError, ValidationError } from '../types/auth';

export interface AuthContextType extends AuthState {
  // Returns the raw login response so forms can react to MFA challenge
  // shapes (mfaRequired / mfaSetupRequired) instead of assuming a session.
  login: (email: string, password: string, rememberMe?: boolean) => Promise<MfaLoginResponse | null>;
  // Establishes the session after /auth/mfa/verify or /auth/mfa/activate.
  completeMfaLogin: (user: User) => void;
  register: (data: Omit<RegisterData, 'confirmPassword'>) => Promise<{ requiresVerification: boolean }>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<RegisterData>) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyResetToken: (token: string) => Promise<{ valid: boolean }>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  clearValidationErrors: () => void;
  clearRateLimit: () => void;
  hasRole: (role: string) => boolean;
  retryLastAction: () => Promise<void>;
}

// =====================================================
// ACTIONS
// =====================================================

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_STOP_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User } }
  | { type: 'AUTH_FAILURE'; payload: AuthError }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_CLEAR_ERROR' }
  | { type: 'AUTH_CLEAR_VALIDATION_ERRORS' }
  | { type: 'AUTH_UPDATE_USER'; payload: User }
  | { type: 'AUTH_SET_VALIDATION_ERRORS'; payload: ValidationError[] };

// =====================================================
// REDUCER
// =====================================================

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        validationErrors: [],
      };
    case 'AUTH_STOP_LOADING':
      return {
        ...state,
        isLoading: false,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        validationErrors: [],
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        validationErrors: [],
      };
    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'AUTH_CLEAR_VALIDATION_ERRORS':
      return {
        ...state,
        validationErrors: [],
      };
    case 'AUTH_UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'AUTH_SET_VALIDATION_ERRORS':
      return {
        ...state,
        validationErrors: action.payload,
      };
    default:
      return state;
  }
};

// =====================================================
// INITIAL STATE
// =====================================================

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  // We must validate the httpOnly cookie against the server on boot.
  isLoading: true,
  error: null,
  validationErrors: [],
};

// =====================================================
// CONTEXT
// =====================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  // Holds the most recent async auth action so it can be retried.
  const lastActionRef = useRef<(() => Promise<unknown>) | null>(null);

  // =====================================================
  // EFFECTS
  // =====================================================

  // Validate the httpOnly session cookie by fetching the profile.
  useEffect(() => {
    let cancelled = false;

    const initializeAuth = async () => {
      dispatch({ type: 'AUTH_START' });
      try {
        const user = await authAPI.getProfile();
        if (cancelled) return;

        if (!user.role || !user.role.name) {
          throw new Error('User profile missing role data');
        }

        // Preserve whichever storage the session flag already uses.
        if (!isAuthFlagSet()) {
          setAuthFlag(false);
        }

        dispatch({ type: 'AUTH_SUCCESS', payload: { user } });
      } catch (error) {
        if (cancelled) return;
        clearAuthFlag();
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    initializeAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-refresh the session cookie periodically while authenticated.
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const refreshInterval = setInterval(async () => {
      try {
        await authAPI.refreshToken();
      } catch {
        // Don't logout immediately; the next API call will surface a 401.
      }
    }, 15 * 60 * 1000); // every 15 minutes

    return () => clearInterval(refreshInterval);
  }, [state.isAuthenticated]);

  // =====================================================
  // ACTIONS
  // =====================================================

  const login = async (email: string, password: string, rememberMe = false): Promise<MfaLoginResponse | null> => {
    const loginAction = async () => {
      dispatch({ type: 'AUTH_START' });

      try {
        const response = await authAPI.login({ email, password, rememberMe });

        // SEC task 5: MFA challenge shapes — NO session yet. The caller
        // (login form) must drive the /auth/mfa/verify or /auth/mfa/activate
        // step, then call completeMfaLogin() with the returned user.
        if (response.mfaRequired || response.mfaSetupRequired) {
          dispatch({ type: 'AUTH_STOP_LOADING' });
          return response;
        }

        // The JWT is set as an httpOnly cookie by the server. We only record a
        // non-sensitive flag so non-React modules know a session exists.
        setAuthFlag(Boolean(rememberMe));

        dispatch({ type: 'AUTH_SUCCESS', payload: { user: response.user } });
        return response;
      } catch (error) {
        const authError = ErrorHandler.isAuthError(error)
          ? error
          : ErrorHandler.parseApiError(error);

        // Persist the rate-limit expiry timestamp in a stable place so
        // reloading the page or changing fields keeps the submit button
        // disabled until the lockout truly ends.
        if (
          authError.code === AUTH_ERROR_CODES.TOO_MANY_ATTEMPTS ||
          authError.code === AUTH_ERROR_CODES.RATE_LIMIT_EXCEEDED
        ) {
          const until = ErrorHandler.getRetryAfterSeconds(authError);
          if (until != null && until > 0) {
            sessionStorage.setItem('loginRateLimitUntil', String(Date.now() + until * 1000));
          }
        }

        dispatch({ type: 'AUTH_FAILURE', payload: authError });
        throw authError;
      }
    };

    lastActionRef.current = loginAction;
    return await loginAction();
  };

  // SEC task 5: session established after a successful TOTP verify/activate.
  const completeMfaLogin = (user: User): void => {
    // MFA-verified logins are never "remember me" (backend enforces it too).
    setAuthFlag(false);
    dispatch({ type: 'AUTH_SUCCESS', payload: { user } });
  };

  const register = async (
    data: Omit<RegisterData, 'confirmPassword'>
  ): Promise<{ requiresVerification: boolean }> => {
    try {
      dispatch({ type: 'AUTH_START' });

      await authAPI.register(data);

      // Strict backend: registration NEVER establishes a session; the user
      // must verify their email first, then log in.
      dispatch({ type: 'AUTH_STOP_LOADING' });
      return { requiresVerification: true };
    } catch (error) {
      const authError = ErrorHandler.isAuthError(error)
        ? error
        : ErrorHandler.parseApiError(error);
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      throw authError;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authAPI.logout();
    } catch {
      // Logout failure is non-fatal; the local session is cleared regardless.
    } finally {
      clearAuthFlag();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const updateProfile = async (data: Partial<RegisterData>): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const updatedUser = await authAPI.updateProfile(data);

      dispatch({ type: 'AUTH_UPDATE_USER', payload: updatedUser });
    } catch (error) {
      const authError = ErrorHandler.isAuthError(error)
        ? error
        : ErrorHandler.parseApiError(error);
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      throw authError;
    }
  };

  const forgotPassword = async (email: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      await authAPI.forgotPassword(email);
      dispatch({ type: 'AUTH_STOP_LOADING' });
    } catch (error) {
      const authError = ErrorHandler.isAuthError(error)
        ? error
        : ErrorHandler.parseApiError(error);
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      throw authError;
    }
  };

  const resetPassword = async (token: string, password: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      await authAPI.resetPassword(token, password);
      dispatch({ type: 'AUTH_STOP_LOADING' });
    } catch (error) {
      const authError = ErrorHandler.isAuthError(error)
        ? error
        : ErrorHandler.parseApiError(error);
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      throw authError;
    }
  };

  const verifyResetToken = useCallback(async (token: string): Promise<{ valid: boolean }> => {
    return authAPI.verifyResetToken(token);
  }, []);

  const verifyEmail = async (token: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      await authAPI.verifyEmail(token);
      dispatch({ type: 'AUTH_STOP_LOADING' });
    } catch (error) {
      const authError = ErrorHandler.isAuthError(error)
        ? error
        : ErrorHandler.parseApiError(error);
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      throw authError;
    }
  };

  const resendVerification = async (email: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      await authAPI.resendVerification(email);
      dispatch({ type: 'AUTH_STOP_LOADING' });
    } catch (error) {
      const authError = ErrorHandler.isAuthError(error)
        ? error
        : ErrorHandler.parseApiError(error);
      dispatch({ type: 'AUTH_FAILURE', payload: authError });
      throw authError;
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      await authAPI.refreshToken();
    } catch (error) {
      clearAuthFlag();
      dispatch({ type: 'AUTH_LOGOUT' });
      throw error;
    }
  };

  const clearError = useCallback((): void => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  }, []);

  const clearValidationErrors = useCallback((): void => {
    dispatch({ type: 'AUTH_CLEAR_VALIDATION_ERRORS' });
  }, []);

  const clearRateLimit = useCallback((): void => {
    sessionStorage.removeItem('loginRateLimitUntil');
  }, []);

  const retryLastAction = async (): Promise<void> => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
    if (lastActionRef.current) {
      await lastActionRef.current();
    }
  };

  // Role-name based authorization. The role's `permissions` blob is an
  // informational label only — access is decided by role name against
  // the ACCESS_GROUPS matrix (see types/user.ts).
  const hasRole = (role: string): boolean => {
    return state.user?.role?.name === role;
  };

  // =====================================================
  // CONTEXT VALUE
  // =====================================================

  const value: AuthContextType = {
    ...state,
    login,
    completeMfaLogin,
    register,
    logout,
    updateProfile,
    forgotPassword,
    resetPassword,
    verifyResetToken,
    verifyEmail,
    resendVerification,
    refreshToken,
    clearError,
    clearValidationErrors,
    clearRateLimit,
    hasRole,
    retryLastAction,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// =====================================================
// HOOK
// =====================================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
